import { useEffect, useRef, useState } from 'react';
import { sendGoogleAnalyticsEvents } from '@elfsight/lib-google-analytics';
import {
  requestIdleCallback,
  requestIdleCallbackPolyfill,
  useWidgetTokenStore
} from '@elfsight/lib-utils';
import { useEmitAPIEvent } from '@elfsight/widget-js-api';
import { useStorage } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import {
  API_AFTER_LANGUAGE_CHANGE_EVENT_NAME,
  LANGUAGES
} from '../../constants';
import type { LanguageCode } from '../../types';
import { checkLanguageExists } from '../../utils/check-language-exists';
import { checkSameLanguages } from '../../utils/check-same-languages';
import { translateDocumentTitle } from '../translate/translate-document-title';
import { translatePage } from '../translate/translate-page';
import { useTranslateDynamicContent } from '../translate/use-translate-dynamic-content';
import { checkIfLangEqualToSiteLang } from './check-if-lang-equal-to-site-lang';
import { useCustomTranslations } from './custom-translations';
import { getSiteLanguage } from './get-site-language';
import { restorePreviewContent } from './preview-content';

const PREVIEW_LANGUAGE = 'en';

export function useSaveLanguage() {
  const {
    editMode,
    translationExclusions,
    languages,
    googleAnalytics,
    widgetId
  } = useSettings();

  const widgetTokenStore = useWidgetTokenStore();
  const [
    storedLanguage,
    { set: setLocalStorageData, get: getLocalStorageData }
  ] = useStorage<LanguageCode | undefined>('language', undefined);

  const [language, setLanguage] = useState<LanguageCode | undefined>(
    storedLanguage
  );
  const { customTranslations, status } = useCustomTranslations(
    editMode ? language : undefined
  );
  const isCustomTranslationsLoading = status === 'loading';

  const siteLanguage = getSiteLanguage(languages);
  const emitAPIEvent = useEmitAPIEvent();

  const contextRef = useRef({ siteLanguage });

  const emitLanguageChange = (
    previousLanguage: string | undefined,
    currentLanguage: LanguageCode
  ) => {
    return emitAPIEvent(API_AFTER_LANGUAGE_CHANGE_EVENT_NAME, [
      { previousLanguage, currentLanguage }
    ]);
  };

  const isValidLanguage = (language: LanguageCode | undefined) => {
    if (!language || !Array.isArray(languages) || !languages.length) {
      return false;
    }

    if (language === siteLanguage) {
      return true;
    }

    return languages.some((item) => item && item.language === language);
  };

  const updateUrl = (lang: LanguageCode, shouldReload = true) => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('lang', lang);

    window.history.replaceState(null, '', currentUrl.toString());

    if (shouldReload) {
      window.location.reload();
    }
  };

  const resetUrlAndStorageData = () => {
    setLocalStorageData(undefined);

    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete('lang');

    window.history.replaceState(null, '', currentUrl.toString());
    window.location.reload();
  };

  const updateLanguage = async (lang: LanguageCode) => {
    const languageInfo = LANGUAGES[lang];

    sendGoogleAnalyticsEvents({
      eventType: 'languageChanged',
      googleAnalyticsEvents: googleAnalytics,
      editMode,
      valueMapping: {
        widgetId,
        languageCode: lang,
        languageName: languageInfo?.name || lang
      }
    });

    if (editMode) {
      setLanguage(lang);
      return;
    }

    const previousLanguage = getLocalStorageData() || siteLanguage;
    setLocalStorageData(lang);

    await emitLanguageChange(previousLanguage, lang);

    if (checkIfLangEqualToSiteLang(lang, siteLanguage)) {
      resetUrlAndStorageData();
      return;
    }

    updateUrl(lang);
  };

  useEffect(() => {
    if (!language) {
      return;
    }

    if (document.documentElement.lang !== language) {
      document.documentElement.lang = language;
    }
  }, [language]);

  useEffect(() => {
    // Нужно подождать, пока кастомные переводы будут обработаны
    if (isCustomTranslationsLoading) {
      return;
    }

    if (editMode) {
      const locale = window.navigator.language;
      const userLanguage = checkSameLanguages(locale);
      const isUserLanguageExistInApp = checkLanguageExists(userLanguage);
      const isStateLanguageValid =
        language !== undefined && checkLanguageExists(language);
      const fallbackLanguage = isUserLanguageExistInApp
        ? userLanguage
        : undefined;
      const targetLanguage = isStateLanguageValid ? language : fallbackLanguage;

      if (!targetLanguage) {
        return;
      }

      requestIdleCallback(() => {
        restorePreviewContent();

        translatePage({
          sourceLanguage: PREVIEW_LANGUAGE,
          targetLanguage,
          widgetToken: widgetTokenStore.get(),
          translationExclusions,
          customTranslations
        });
      });

      return;
    }

    const { siteLanguage: siteLanguageFromContext } = contextRef.current;

    const url = new URL(window.location.href);
    const urlLang = url.searchParams.get('lang') as LanguageCode | undefined;
    const localStorageLang = getLocalStorageData();

    const targetLanguage = urlLang || localStorageLang;

    if (!targetLanguage) {
      return;
    }

    setLanguage(targetLanguage);

    const shouldUpdateLanguageInUrl =
      !url.hash &&
      localStorageLang &&
      !checkIfLangEqualToSiteLang(localStorageLang, siteLanguageFromContext);
    const previousLanguage = localStorageLang ?? siteLanguageFromContext;
    const shouldEmitEvent =
      urlLang && urlLang !== previousLanguage && isValidLanguage(urlLang);

    if (urlLang) {
      const isLocalStorageOutdated =
        (!localStorageLang || urlLang !== localStorageLang) &&
        isValidLanguage(urlLang);

      if (shouldEmitEvent) {
        emitLanguageChange(previousLanguage, urlLang);
      }

      if (isLocalStorageOutdated) {
        setLocalStorageData(urlLang);
      }
    } else if (shouldUpdateLanguageInUrl) {
      updateUrl(localStorageLang, false);
    }

    requestIdleCallbackPolyfill(() => {
      translateDocumentTitle({
        sourceLanguage: siteLanguageFromContext,
        targetLanguage,
        widgetToken: widgetTokenStore.get()
      });

      translatePage({
        sourceLanguage: siteLanguageFromContext,
        targetLanguage,
        widgetToken: widgetTokenStore.get(),
        translationExclusions,
        customTranslations
      });
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editMode,
    language,
    translationExclusions,
    getLocalStorageData,
    isCustomTranslationsLoading
  ]);

  useTranslateDynamicContent({
    sourceLanguage: siteLanguage,
    targetLanguage: language,
    customTranslations
  });

  return updateLanguage;
}
